<template>
    <v-card class="pa-8" rounded="lg" color="primary-gray">
        <v-row>
            <v-col cols="12">
                <v-row align="center" class="mb-4">
                    <v-col cols="6">
                        <span class="text-h4">Gerenciamento de Usuários</span>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                        <v-btn color="primary" @click="openCreateUserDialog" class="mr-2">
                            <v-icon left>mdi-account-plus</v-icon>
                            Novo Usuário
                        </v-btn>
                        <v-btn-toggle v-model="filterStatus" mandatory class="mr-2">
                            <v-btn small value="all">Todos</v-btn>
                            <v-btn small value="pro">Pro</v-btn>
                            <v-btn small value="starter">Starter</v-btn>
                            <v-btn small value="expiring">Expirando</v-btn>
                            <v-btn small value="blocked">Bloqueados</v-btn>
                        </v-btn-toggle>
                        <v-btn icon @click="loadUsers">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="filteredUsers"
                    :loading="loading"
                    class="elevation-1"
                    sort-by="name"
                >
                    <template v-slot:[`item.name`]="{ item }">
                        <div class="d-flex align-center">
                            <v-avatar size="32" class="mr-2">
                                <v-icon v-if="!item.user_image">mdi-account</v-icon>
                                <v-img v-else :src="item.user_image" />
                            </v-avatar>
                            <div>
                                <div class="font-weight-medium">{{ item.name }}</div>
                                <div class="text-caption grey--text">{{ item.email }}</div>
                            </div>
                        </div>
                    </template>

                    <template v-slot:[`item.profile`]="{ item }">
                        <v-chip
                            small
                            :color="getProfileColor(item)"
                            text-color="white"
                        >
                            <v-icon left small>{{ getProfileIcon(item) }}</v-icon>
                            {{ getProfileLabel(item) }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.expires_at`]="{ item }">
                        <span v-if="!item.profile_expires_at">
                            <v-chip small color="success" text-color="white">
                                Permanente
                            </v-chip>
                        </span>
                        <span v-else>
                            <div>{{ formatDate(item.profile_expires_at) }}</div>
                            <div class="text-caption" :class="getDaysRemainingColor(item)">
                                {{ getDaysRemainingText(item) }}
                            </div>
                        </span>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="openEditUserDialog(item)">
                                    <v-list-item-icon>
                                        <v-icon color="primary">mdi-pencil</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Editar Usuário</v-list-item-title>
                                </v-list-item>

                                <v-divider></v-divider>

                                <v-list-item @click="openGrantProDialog(item, 'pro')" v-if="!isPro(item)">
                                    <v-list-item-icon>
                                        <v-icon color="success">mdi-star-plus</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Conceder Premium</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="openGrantProDialog(item, 'starter')" v-if="!isStarter(item) && !isPro(item)">
                                    <v-list-item-icon>
                                        <v-icon color="info">mdi-account-plus</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Conceder Starter</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="openExtendProDialog(item)" v-if="(isPro(item) || isStarter(item)) && item.profile_expires_at">
                                    <v-list-item-icon>
                                        <v-icon color="info">mdi-clock-plus</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Estender Prazo</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="makeProPermanent(item)" v-if="(isPro(item) || isStarter(item)) && item.profile_expires_at">
                                    <v-list-item-icon>
                                        <v-icon color="primary">mdi-infinity</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Tornar Permanente</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="revokeAccess(item)" v-if="isPro(item) || isStarter(item)">
                                    <v-list-item-icon>
                                        <v-icon color="error">mdi-lock</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Revogar / Bloquear</v-list-item-title>
                                </v-list-item>

                                <v-divider></v-divider>

                                <v-list-item @click="confirmDeleteUser(item)">
                                    <v-list-item-icon>
                                        <v-icon color="error">mdi-account-remove</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Inativar Usuário</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- Dialog: Conceder Pro -->
        <v-dialog v-model="grantProDialog" max-width="500">
            <v-card>
                <v-card-title>Conceder Acesso {{ grantProfileSlug === 'pro' ? 'Premium' : 'Starter' }}</v-card-title>
                <v-card-text>
                    <p>Usuário: <strong>{{ selectedUser?.name }}</strong></p>
                    
                    <v-checkbox
                        v-model="isPermanentGrant"
                        :label="`Acesso ${grantProfileSlug === 'pro' ? 'Premium' : 'Starter'} Permanente (sem expiração)`"
                        class="mt-0"
                    ></v-checkbox>
                    
                    <v-menu
                        v-if="!isPermanentGrant"
                        v-model="datePickerMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formattedExpirationDate"
                                label="Data de Expiração"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="expirationDate"
                            @input="datePickerMenu = false"
                            :min="minDate"
                        ></v-date-picker>
                    </v-menu>
                    
                    <v-checkbox
                        v-model="notifyUser"
                        label="Notificar usuário por email"
                    ></v-checkbox>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="grantProDialog = false">Cancelar</v-btn>
                    <v-btn color="success" @click="confirmGrantPro" :loading="actionLoading">Conceder</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Estender Prazo -->
        <v-dialog v-model="extendProDialog" max-width="500">
            <v-card>
                <v-card-title>Estender Prazo Pro</v-card-title>
                <v-card-text>
                    <p>Usuário: <strong>{{ selectedUser?.name }}</strong></p>
                    <p>Expira em: <strong>{{ formatDate(selectedUser?.profile_expires_at) }}</strong></p>
                    <v-menu
                        v-model="extendDatePickerMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formattedNewExpirationDate"
                                label="Nova Data de Expiração"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="newExpirationDate"
                            @input="extendDatePickerMenu = false"
                            :min="minExtendDate"
                        ></v-date-picker>
                    </v-menu>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="extendProDialog = false">Cancelar</v-btn>
                    <v-btn color="info" @click="confirmExtendPro" :loading="actionLoading">Estender</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Confirmação -->
        <v-dialog v-model="confirmDialog" max-width="400">
            <v-card>
                <v-card-title>{{ confirmTitle }}</v-card-title>
                <v-card-text>
                    <p>{{ confirmMessage }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
                    <v-btn :color="confirmColor" @click="executeConfirmAction">Confirmar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Criar/Editar Usuário -->
        <v-dialog v-model="userDialog" max-width="600">
            <v-card>
                <v-card-title>
                    {{ isEditMode ? 'Editar Usuário' : 'Novo Usuário' }}
                </v-card-title>
                <v-card-text>
                    <v-form ref="userForm">
                        <v-text-field
                            v-model="userForm.name"
                            label="Nome Completo *"
                            :rules="[v => !!v || 'Nome é obrigatório']"
                            required
                        ></v-text-field>

                        <v-text-field
                            v-model="userForm.email"
                            label="E-mail *"
                            type="email"
                            :rules="[
                                v => !!v || 'E-mail é obrigatório',
                                v => /.+@.+\..+/.test(v) || 'E-mail inválido'
                            ]"
                            required
                        ></v-text-field>

                        <v-text-field
                            v-if="!isEditMode"
                            v-model="userForm.password"
                            label="Senha *"
                            type="password"
                            :rules="[
                                v => !!v || 'Senha é obrigatória',
                                v => v.length >= 8 || 'Senha deve ter no mínimo 8 caracteres'
                            ]"
                            required
                        ></v-text-field>

                        <v-text-field
                            v-if="isEditMode"
                            v-model="userForm.password"
                            label="Nova Senha (deixe em branco para não alterar)"
                            type="password"
                            :rules="[
                                v => !v || v.length >= 8 || 'Senha deve ter no mínimo 8 caracteres'
                            ]"
                        ></v-text-field>

                        <v-select
                            v-model="userForm.role"
                            :items="roleOptions"
                            label="Papel *"
                            :rules="[v => !!v || 'Papel é obrigatório']"
                            required
                        ></v-select>

                        <v-text-field
                            v-model="userForm.phone"
                            label="Telefone"
                            placeholder="(11) 98765-4321"
                        ></v-text-field>

                        <v-text-field
                            v-model="userForm.crp"
                            label="CRP"
                            placeholder="06/12345"
                        ></v-text-field>

                        <v-text-field
                            v-model="userForm.address"
                            label="Endereço"
                        ></v-text-field>

                        <v-select
                            v-model="userForm.access_profile_id"
                            :items="profileOptions"
                            item-text="name"
                            item-value="id"
                            label="Perfil de Acesso *"
                            :rules="[v => !!v || 'Perfil é obrigatório']"
                            required
                        ></v-select>

                        <v-select
                            v-model="userForm.services"
                            :items="services"
                            item-text="name"
                            item-value="id"
                            label="Serviços Habilitados"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                        ></v-select>

                        <v-checkbox
                            v-model="userForm.is_sysadmin"
                            label="Administrador do Sistema (SysAdmin)"
                        ></v-checkbox>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="userDialog = false">Cancelar</v-btn>
                    <v-btn 
                        :color="isEditMode ? 'primary' : 'success'" 
                        @click="saveUser" 
                        :loading="actionLoading"
                    >
                        {{ isEditMode ? 'Salvar' : 'Criar' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';

export default {
    name: 'UsersManagementPage',
    
    data() {
        return {
            loading: false,
            actionLoading: false,
            filterStatus: 'all',
            headers: [
                { text: 'Usuário', value: 'name' },
                { text: 'Perfil', value: 'profile', align: 'center' },
                { text: 'Expira em', value: 'expires_at', align: 'center' },
                { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
            ],
            grantProDialog: false,
            extendProDialog: false,
            datePickerMenu: false,
            extendDatePickerMenu: false,
            selectedUser: null,
            expirationDate: null,
            newExpirationDate: null,
            notifyUser: false,
            isPermanentGrant: false,
            grantProfileSlug: 'pro',
            // Dialogs de confirmação
            confirmDialog: false,
            confirmTitle: '',
            confirmMessage: '',
            confirmAction: null,
            confirmColor: 'primary',
            // Dialog de criar/editar usuário
            userDialog: false,
            isEditMode: false,
            userForm: {
                name: '',
                email: '',
                password: '',
                role: 'doctor',
                phone: '',
                crp: '',
                address: '',
                access_profile_id: 1,
                is_sysadmin: false,
                services: []
            },
            roleOptions: [
                { text: 'Médico/Profissional', value: 'doctor' },
                { text: 'Administrador', value: 'admin' }
            ],
            profileOptions: [
                { id: 1, name: 'Starter' },
                { id: 2, name: 'Pro' }
            ]
        };
    },
    
    computed: {
        ...mapState('admin', ['users', 'services']),
        
        filteredUsers() {
            if (this.filterStatus === 'all') return this.users;
            
            return this.users.filter(user => {
                const profile = user.access_profile;
                
                if (this.filterStatus === 'pro') {
                    return profile?.slug === 'pro';
                }
                
                if (this.filterStatus === 'starter') {
                    return profile?.slug === 'starter';
                }
                
                if (this.filterStatus === 'blocked') {
                    return !profile;
                }
                
                if (this.filterStatus === 'expiring') {
                    if (!user.profile_expires_at) return false;
                    const daysRemaining = this.getDaysRemaining(user);
                    return daysRemaining >= 0 && daysRemaining <= 30;
                }
                
                return true;
            });
        },
        
        minDate() {
            return dayjs().format('YYYY-MM-DD');
        },
        
        minExtendDate() {
            if (!this.selectedUser?.profile_expires_at) return this.minDate;
            return dayjs(this.selectedUser.profile_expires_at).format('YYYY-MM-DD');
        },
        
        formattedExpirationDate() {
            if (!this.expirationDate) return '';
            return dayjs(this.expirationDate).format('DD/MM/YYYY');
        },
        
        formattedNewExpirationDate() {
            if (!this.newExpirationDate) return '';
            return dayjs(this.newExpirationDate).format('DD/MM/YYYY');
        }
    },
    
    mounted() {
        this.loadUsers();
    },
    
    methods: {
        ...mapActions('admin', [
            'ActionGetUsers',
            'ActionCreateUser',
            'ActionUpdateUser',
            'ActionDeleteUser',
            'ActionGrantPro',
            'ActionExtendPro',
            'ActionMakeProPermanent',
            'ActionRevokePro',
            'ActionGetServices'
        ]),
        
        async loadUsers() {
            this.loading = true;
            try {
                await Promise.all([
                    this.ActionGetUsers(),
                    this.ActionGetServices()
                ]);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                this.loading = false;
            }
        },
        
        showNotification(message, type = 'success') {
            // Usar EventBus ou console para notificações
            console.log(`[${type.toUpperCase()}] ${message}`);
            // TODO: Implementar sistema de notificação global
        },
        
        showConfirm(title, message, action, color = 'primary') {
            this.confirmTitle = title;
            this.confirmMessage = message;
            this.confirmAction = action;
            this.confirmColor = color;
            this.confirmDialog = true;
        },
        
        async executeConfirmAction() {
            if (this.confirmAction) {
                await this.confirmAction();
            }
            this.confirmDialog = false;
        },
        
        isPro(user) {
            return user.access_profile?.slug === 'pro';
        },
        
        isStarter(user) {
            return user.access_profile?.slug === 'starter';
        },
        
        getProfileColor(user) {
            const profile = user.access_profile;
            if (!profile) return 'grey';
            
            if (profile.slug === 'starter') return 'grey';
            
            // Pro
            if (!user.profile_expires_at) return 'success'; // Permanente
            
            const daysRemaining = this.getDaysRemaining(user);
            if (daysRemaining < 0) return 'error';
            if (daysRemaining <= 7) return 'warning';
            return 'success';
        },
        
        getProfileIcon(user) {
            const profile = user.access_profile;
            if (!profile || profile.slug === 'starter') return 'mdi-account';
            return 'mdi-star';
        },
        
        getProfileLabel(user) {
            const profile = user.access_profile;
            if (!profile) return 'Bloqueado';
            return profile.name;
        },
        
        getDaysRemaining(user) {
            if (!user.profile_expires_at) return null;
            const expirationDate = new Date(user.profile_expires_at);
            const now = new Date();
            const diffTime = expirationDate - now;
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        },
        
        getDaysRemainingText(user) {
            const days = this.getDaysRemaining(user);
            if (days === null) return '';
            if (days < 0) return 'Expirado';
            if (days === 0) return 'Expira hoje';
            if (days === 1) return 'Expira amanhã';
            return `${days} dias restantes`;
        },
        
        getDaysRemainingColor(user) {
            const days = this.getDaysRemaining(user);
            if (days === null) return '';
            if (days < 0) return 'error--text';
            if (days <= 7) return 'warning--text';
            return 'success--text';
        },
        
        formatDate(date) {
            if (!date) return '-';
            return dayjs(date).format('DD/MM/YYYY');
        },
        
        openGrantProDialog(user, profileSlug = 'pro') {
            this.selectedUser = user;
            this.grantProfileSlug = profileSlug;
            this.expirationDate = dayjs().add(30, 'days').format('YYYY-MM-DD');
            this.notifyUser = false;
            this.isPermanentGrant = false;
            this.grantProDialog = true;
        },
        
        openExtendProDialog(user) {
            this.selectedUser = user;
            this.newExpirationDate = dayjs(user.profile_expires_at).add(30, 'days').format('YYYY-MM-DD');
            this.extendProDialog = true;
        },
        
        async confirmGrantPro() {
            this.actionLoading = true;
            try {
                // Se for permanente, envia null, senão envia a data
                const expiresAt = this.isPermanentGrant 
                    ? null 
                    : dayjs(this.expirationDate).format('YYYY-MM-DD 23:59:59');
                
                await this.ActionGrantPro({
                    userId: this.selectedUser.id,
                    expiresAt,
                    notifyUser: this.notifyUser,
                    profileSlug: this.grantProfileSlug
                });
                
                const planName = this.grantProfileSlug === 'pro' ? 'Premium' : 'Starter';
                const message = this.isPermanentGrant 
                    ? `Acesso ${planName} permanente concedido com sucesso` 
                    : `Acesso ${planName} concedido com sucesso`;
                this.showNotification(message, 'success');
                this.grantProDialog = false;
                await this.loadUsers();
            } catch (error) {
                this.showNotification(error.response?.data?.message || 'Erro ao conceder Pro', 'error');
            } finally {
                this.actionLoading = false;
            }
        },
        
        async confirmExtendPro() {
            this.actionLoading = true;
            try {
                const newExpiresAt = dayjs(this.newExpirationDate).format('YYYY-MM-DD 23:59:59');
                await this.ActionExtendPro({
                    userId: this.selectedUser.id,
                    newExpiresAt
                });
                
                this.showNotification('Prazo estendido com sucesso', 'success');
                this.extendProDialog = false;
                await this.loadUsers();
            } catch (error) {
                this.showNotification(error.response?.data?.message || 'Erro ao estender prazo', 'error');
            } finally {
                this.actionLoading = false;
            }
        },
        
        makeProPermanent(user) {
            this.showConfirm(
                'Confirmar',
                `Tornar acesso Pro permanente para ${user.name}?`,
                async () => {
                    try {
                        await this.ActionMakeProPermanent(user.id);
                        this.showNotification('Acesso Pro permanente concedido', 'success');
                        await this.loadUsers();
                    } catch (error) {
                        this.showNotification(error.response?.data?.message || 'Erro ao tornar Pro permanente', 'error');
                    }
                },
                'primary'
            );
        },
        
        // ===== CRUD de Usuários =====
        
        openCreateUserDialog() {
            this.isEditMode = false;
            this.userForm = {
                name: '',
                email: '',
                password: '',
                role: 'doctor',
                phone: '',
                crp: '',
                address: '',
                access_profile_id: 1,
                is_sysadmin: false,
                services: []
            };
            this.userDialog = true;
        },

        openEditUserDialog(user) {
            this.isEditMode = true;
            this.userForm = {
                id: user.id,
                name: user.name,
                email: user.email,
                password: '',
                role: user.role,
                phone: user.phone || '',
                crp: user.crp || '',
                address: user.address || '',
                access_profile_id: user.access_profile_id,
                is_sysadmin: user.is_sysadmin || false,
                services: user.services ? user.services.map(s => s.id) : []
            };
            this.userDialog = true;
        },

        async saveUser() {
            if (!this.$refs.userForm.validate()) {
                return;
            }

            this.actionLoading = true;
            try {
                // Preparar dados (remover password vazio no edit)
                const userData = { ...this.userForm };
                if (this.isEditMode && !userData.password) {
                    delete userData.password;
                }
                delete userData.id; // Não enviar ID no body

                if (this.isEditMode) {
                    await this.ActionUpdateUser({
                        userId: this.userForm.id,
                        userData
                    });
                    this.showNotification('Usuário atualizado com sucesso', 'success');
                } else {
                    await this.ActionCreateUser(userData);
                    this.showNotification('Usuário criado com sucesso', 'success');
                }

                this.userDialog = false;
                await this.loadUsers();
            } catch (error) {
                const message = error.response?.data?.message || 
                               error.response?.data?.errors?.email?.[0] ||
                               `Erro ao ${this.isEditMode ? 'atualizar' : 'criar'} usuário`;
                this.showNotification(message, 'error');
            } finally {
                this.actionLoading = false;
            }
        },

        confirmDeleteUser(user) {
            this.showConfirm(
                'Confirmar Inativação',
                `Inativar usuário ${user.name}? Esta ação não pode ser desfeita.`,
                async () => {
                    try {
                        await this.ActionDeleteUser(user.id);
                        this.showNotification('Usuário inativado com sucesso', 'success');
                        await this.loadUsers();
                    } catch (error) {
                        const message = error.response?.data?.error || 
                                       'Erro ao inativar usuário';
                        this.showNotification(message, 'error');
                    }
                },
                'error'
            );
        },
        
        revokeAccess(user) {
            const currentPlan = user.access_profile?.name || 'desconhecido';
            this.showConfirm(
                'Confirmar Revogação',
                `Revogar acesso de ${user.name} (plano ${currentPlan})? O usuário será completamente bloqueado e precisará contratar um novo plano.`,
                async () => {
                    try {
                        await this.ActionRevokePro(user.id);
                        this.showNotification('Acesso revogado - usuário bloqueado', 'success');
                        await this.loadUsers();
                    } catch (error) {
                        this.showNotification(error.response?.data?.message || 'Erro ao revogar acesso', 'error');
                    }
                },
                'error'
            );
        }
    }
};
</script>
